import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n";
import { PROJECTS } from "@/data/projects";

const AUTOPLAY_MS = 4500;

export default function VenturesCarousel() {
  const { lang, t } = useLanguage();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  const timer = useRef(null);

  const play = useCallback(() => {
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, AUTOPLAY_MS);
  }, [emblaApi]);

  const stop = useCallback(() => clearInterval(timer.current), []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    play();
    return () => {
      stop();
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, play, stop]);

  const goTo = (i) => {
    if (emblaApi) emblaApi.scrollTo(i);
    play();
  };
  const prev = () => {
    if (emblaApi) emblaApi.scrollPrev();
    play();
  };
  const next = () => {
    if (emblaApi) emblaApi.scrollNext();
    play();
  };

  return (
    <section className="relative bg-ebony py-20 md:py-28 overflow-hidden" data-testid="ventures-carousel">
      <div className="absolute inset-0 pattern-kitenge" />
      <div className="relative max-w-7xl mx-auto">
        <div className="px-6 lg:px-12 flex items-end justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-ochre mb-3" data-testid="carousel-overline">
              {t.carousel.overline}
            </p>
            <p className="font-serif text-2xl md:text-3xl font-light tracking-tight text-bone">
              KEDE <span className="italic text-ochre">01–07</span>
            </p>
          </div>
          <div className="hidden sm:flex gap-3">
            <button
              onClick={prev}
              data-testid="carousel-prev"
              aria-label="Previous"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-bone/25 text-bone hover:bg-terracotta hover:border-terracotta transition-colors duration-300"
            >
              <ArrowLeft size={17} />
            </button>
            <button
              onClick={next}
              data-testid="carousel-next"
              aria-label="Next"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-bone/25 text-bone hover:bg-terracotta hover:border-terracotta transition-colors duration-300"
            >
              <ArrowRight size={17} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef} onMouseEnter={stop} onMouseLeave={play} data-testid="carousel-viewport">
          <div className="flex">
            {PROJECTS.map((p) => (
              <div key={p.slug} className="flex-[0_0_100%] min-w-0 px-6 lg:px-12" data-testid={`carousel-slide-${p.slug}`}>
                <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
                  <div className="md:col-span-5 order-2 md:order-1">
                    <span className="text-[10px] uppercase tracking-[0.3em] text-ochre">{p.index} / 07</span>
                    <h3 className="mt-3 font-serif text-4xl md:text-6xl font-light tracking-tighter text-bone">{p.name}</h3>
                    <p className="mt-4 text-sm md:text-base font-light leading-relaxed text-bone/70 max-w-md">{p[lang].tagline}</p>
                    <Link
                      to={`/${lang}/ventures/${p.slug}`}
                      data-testid={`carousel-link-${p.slug}`}
                      className="group mt-8 inline-flex items-center gap-3 rounded-full border border-bone/30 px-6 py-3 text-sm font-light text-bone hover:bg-terracotta hover:border-terracotta transition-colors duration-500"
                    >
                      {t.carousel.explore}
                      <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1" />
                    </Link>
                  </div>
                  <div className="md:col-span-7 order-1 md:order-2">
                    <div className="relative overflow-hidden rounded-md h-64 sm:h-80 md:h-[26rem]">
                      <img src={p.image} alt={p.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-ebony/10" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 md:mt-12 px-6 lg:px-12 flex items-center gap-2.5" data-testid="carousel-dots">
          {PROJECTS.map((p, i) => (
            <button
              key={p.slug}
              onClick={() => goTo(i)}
              data-testid={`carousel-dot-${p.slug}`}
              aria-label={p.name}
              className={`h-1.5 rounded-full transition-[width,background-color] duration-500 ${
                selected === i ? "w-9 bg-ochre" : "w-3 bg-bone/25 hover:bg-bone/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
